import React from "react";
import { useTranslation } from "react-i18next";
import { GraphUser, ExtensionsStatusData, ExtensionType } from "../../types";

interface SecurityGroupSectionProps {
  extensionsStatus: ExtensionsStatusData | null;
  allowedSecurityGroup: GraphUser | null;
  saving: boolean;
  verifyingExtension: ExtensionType | null;
  onActivateExtension: (extensionKey: ExtensionType) => void;
  onVerifyExtension: (extensionKey: ExtensionType) => void;
  onOpenPicker: () => void;
  onShowRemoveConfirm: () => void;
}

export const SecurityGroupSection: React.FC<SecurityGroupSectionProps> = ({
  extensionsStatus,
  allowedSecurityGroup,
  saving,
  verifyingExtension,
  onActivateExtension,
  onVerifyExtension,
  onOpenPicker,
  onShowRemoveConfirm,
}) => {
  const { t } = useTranslation('adminPage');

  const isPremium = extensionsStatus?.featureDetails?.find(
    (f) => f.key === 'entra_id_group_creation_lock'
  )?.isPremium ?? true;

  const hasPaidAccess = !isPremium || Boolean(extensionsStatus?.isPaidTier);

  return (
    <div className="setup-section">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
        <h2>{t('sections.securityGroup.title')}</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          {isPremium && (
            <span className="admin-premium-badge">
              {t('sections.securityGroup.premiumBadge')}
            </span>
          )}
          {hasPaidAccess && (
            extensionsStatus?.extensions?.directory?.consented ? (
              <span className="admin-extension-badge">
                {t('sections.securityGroup.extensionActive')}
              </span>
            ) : (
              <span className="admin-premium-badge">
                ⚠️ {t('sections.securityGroup.extensionRequiredTitle')}
              </span>
            )
          )}
        </div>
      </div>
      <p className="help-text">{t('sections.securityGroup.description')}</p>

      <div className="status-card">
        <div className="status-content">
          {!hasPaidAccess ? (
            /* Cas 1 : Client Gratuit (si feature premium) */
            <div className="admin-extension-banner premium">
              <div className="admin-extension-banner-header">
                <strong>{t('sections.securityGroup.premiumBadge')}</strong>
              </div>
              <p className="admin-extension-banner-desc">
                {t('sections.securityGroup.premiumDescription')}
              </p>
              <div className="admin-extension-actions">
                <button
                  className="btn btn-primary btn-small"
                  onClick={() => {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  {t('sections.securityGroup.upgradeButton')}
                </button>
              </div>
            </div>
          ) : !extensionsStatus?.extensions?.directory?.consented ? (
            /* Cas 2 : Client avec accès sans extension Directory consentie */
            <div className="admin-extension-banner warning">
              <div className="admin-extension-banner-header">
                <strong>⚠️ {t('sections.securityGroup.extensionRequiredTitle')}</strong>
              </div>
              <p className="admin-extension-banner-desc">
                {t('sections.securityGroup.extensionRequiredDescription')}
              </p>
              <div className="admin-extension-actions">
                <button
                  className="btn btn-primary btn-small"
                  onClick={() => onActivateExtension('directory')}
                >
                  {t('sections.securityGroup.activateExtension')}
                </button>
                <button
                  className="btn btn-secondary btn-small"
                  onClick={() => onVerifyExtension('directory')}
                  disabled={verifyingExtension === 'directory'}
                >
                  {verifyingExtension === 'directory' ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      <span className="admin-spinner-inline" />
                      {t('sections.securityGroup.verifying')}
                    </span>
                  ) : (
                    t('sections.securityGroup.verifyExtension')
                  )}
                </button>
              </div>
            </div>
          ) : (
            /* Cas 3 : Client Payant avec extension Directory consentie */
            <div>
              {allowedSecurityGroup ? (
                <div>
                  <div className="admin-extension-banner active">
                    <div className="admin-extension-banner-header">
                      <strong>🔒 {t('sections.securityGroup.configuredTitle')}</strong>
                    </div>
                    <p className="admin-extension-banner-desc">
                      {t('sections.securityGroup.configuredDescription')}
                    </p>
                  </div>

                  <div className="admin-lock-group-box">
                    <div className="admin-lock-grid">
                      <span className="admin-lock-label">{t('sections.securityGroup.name')}</span>
                      <span className="admin-lock-value">{allowedSecurityGroup.displayName}</span>

                      {allowedSecurityGroup.id && (
                        <>
                          <span className="admin-lock-label">{t('sections.securityGroup.id')}</span>
                          <code className="admin-lock-code">{allowedSecurityGroup.id}</code>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="admin-extension-actions" style={{ marginTop: '12px' }}>
                    <button
                      className="btn btn-secondary btn-small"
                      onClick={onOpenPicker}
                      disabled={saving}
                    >
                      {t('sections.securityGroup.changeGroupButton')}
                    </button>
                    <button
                      className="admin-remove-btn"
                      onClick={onShowRemoveConfirm}
                      disabled={saving}
                      style={{ height: '32px', padding: '0 12px' }}
                    >
                      {t('sections.securityGroup.removeGroupButton')}
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="admin-extension-banner premium">
                    <div className="admin-extension-banner-header">
                      <strong>{t('sections.securityGroup.notConfiguredTitle')}</strong>
                    </div>
                    <p className="admin-extension-banner-desc">
                      {t('sections.securityGroup.notConfiguredDescription')}
                    </p>
                  </div>

                  <div className="admin-extension-actions">
                    <button
                      className="btn btn-primary btn-small"
                      onClick={onOpenPicker}
                      disabled={saving}
                    >
                      {t('sections.securityGroup.setGroupButton')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
